import React, { useState, useEffect } from 'react';
import { X, Trophy, Coins, Shield, Crown, ChevronUp, ChevronDown, User } from 'lucide-react';
import { Profile } from '../types';
import { SupabaseService } from '../services/supabaseService';
import { getProfileTitle } from '../utils/gameRules';
import { ProfileDossier } from './ProfileDossier';

interface LeaderboardModalProps {
    isOpen: boolean;
    onClose: () => void;
    currentUserId?: string;
}

type SortField = 'rating' | 'gold' | 'campaigns_completed';

export const LeaderboardModal: React.FC<LeaderboardModalProps> = ({ isOpen, onClose, currentUserId }) => {
    const [players, setPlayers] = useState<Profile[]>([]);
    const [loading, setLoading] = useState(false);
    const [sortField, setSortField] = useState<SortField>('rating');
    const [sortAsc, setSortAsc] = useState(false);

    // Dossier State
    const [selectedProfile, setSelectedProfile] = useState<Profile | null>(null);

    useEffect(() => {
        if (isOpen) {
            loadLeaderboard();
        }
    }, [isOpen]);

    const loadLeaderboard = async () => {
        setLoading(true);
        // Fetch top 100 to allow some sorting flexibility client-side
        const data = await SupabaseService.getLeaderboard(100);
        setPlayers(data);
        setLoading(false);
    };

    const handleSort = (field: SortField) => {
        if (sortField === field) {
            setSortAsc(!sortAsc);
        } else {
            setSortField(field);
            setSortAsc(false); // Default to descending (highest first)
        }
    };

    const sortedPlayers = [...players].sort((a, b) => {
        const valA = a[sortField] || 0;
        const valB = b[sortField] || 0;
        return sortAsc ? valA - valB : valB - valA;
    });

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/95 backdrop-blur-md p-4 animate-fade-in font-mono">

            {/* DOSSIER OVERLAY */}
            {selectedProfile && (
                <ProfileDossier
                    isOpen={true}
                    profile={selectedProfile}
                    onClose={() => setSelectedProfile(null)}
                    onUpgrade={() => { }} // Leaderboard doesn't handle upgrade directly, assumes profile handles it or user goes elsewhere
                />
            )}

            <div className="w-full max-w-4xl h-[80vh] bg-zinc-950 border border-tudor-gold/30 rounded-lg flex flex-col relative shadow-2xl overflow-hidden">
                {/* Header */}
                <div className="p-6 border-b border-zinc-800 bg-zinc-900/50 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <Trophy className="w-8 h-8 text-tudor-gold" />
                        <div>
                            <h2 className="text-2xl font-serif text-tudor-gold tracking-widest uppercase">High Council</h2>
                            <p className="text-xs text-zinc-500 uppercase tracking-[0.2em]">{players.length} Commanders Registered</p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-zinc-500 hover:text-white transition-colors"
                    >
                        <X size={24} />
                    </button>
                </div>

                {/* Table Header */}
                <div className="grid grid-cols-[0.5fr,2fr,1fr,1fr,1fr] gap-4 p-4 border-b border-zinc-800 bg-black/40 text-xs uppercase tracking-widest text-zinc-500 font-bold sticky top-0">
                    <div className="pl-2">Rank</div>
                    <div
                        className="cursor-pointer hover:text-tudor-gold transition-colors flex items-center gap-1"
                    >
                        Commander
                    </div>
                    <div
                        className="cursor-pointer hover:text-tudor-gold transition-colors flex items-center gap-1 justify-end"
                        onClick={() => handleSort('rating')}
                    >
                        <Shield size={12} /> Rating
                        {sortField === 'rating' && (sortAsc ? <ChevronUp size={12} /> : <ChevronDown size={12} />)}
                    </div>
                    <div
                        className="cursor-pointer hover:text-amber-400 transition-colors flex items-center gap-1 justify-end"
                        onClick={() => handleSort('gold')}
                    >
                        <Coins size={12} /> Treasury
                        {sortField === 'gold' && (sortAsc ? <ChevronUp size={12} /> : <ChevronDown size={12} />)}
                    </div>
                    <div
                        className="cursor-pointer hover:text-zinc-300 transition-colors flex items-center gap-1 justify-end"
                        onClick={() => handleSort('campaigns_completed')}
                    >
                        <Crown size={12} /> Campaigns
                        {sortField === 'campaigns_completed' && (sortAsc ? <ChevronUp size={12} /> : <ChevronDown size={12} />)}
                    </div>
                </div>

                {/* List */}
                <div className="flex-1 overflow-y-auto p-2 space-y-1">
                    {loading ? (
                        <div className="flex items-center justify-center h-full text-tudor-gold animate-pulse text-xs uppercase tracking-widest">
                            Fetching Archives...
                        </div>
                    ) : (
                        sortedPlayers.map((p, index) => {
                            const isMe = p.id === currentUserId;
                            const rank = index + 1;

                            return (
                                <div
                                    key={p.id}
                                    onClick={() => setSelectedProfile(p)}
                                    className={`
                                        grid grid-cols-[0.5fr,2fr,1fr,1fr,1fr] gap-4 p-4 rounded items-center cursor-pointer transition-all
                                        ${isMe ? 'bg-tudor-gold/10 border border-tudor-gold/30' : 'hover:bg-zinc-900 border border-transparent hover:border-zinc-800'}
                                    `}
                                >
                                    <div className="font-mono text-zinc-500 pl-2">#{rank}</div>
                                    <div className="flex flex-col">
                                        <div className={`font-serif text-lg ${isMe ? 'text-tudor-gold' : 'text-zinc-200'} flex items-center gap-2`}>
                                            {p.username}
                                            {p.is_premium && <Crown size={12} className="text-tudor-gold" fill="currentColor" />}
                                        </div>
                                        <div className="text-[10px] text-zinc-500 uppercase tracking-widest">{getProfileTitle(p.rating)}</div>
                                    </div>
                                    <div className="text-right font-mono text-zinc-300 font-bold">{p.rating}</div>
                                    <div className="text-right font-mono text-amber-500">{p.gold.toLocaleString()}</div>
                                    <div className="text-right font-mono text-zinc-400">{p.campaigns_completed}</div>
                                </div>
                            );
                        })
                    )}
                </div>

                {/* Footer */}
                <div className="p-4 border-t border-zinc-800 bg-zinc-900/50 text-center text-[10px] text-zinc-500 uppercase tracking-widest">
                    Showing Top {players.length} Commanders
                </div>
            </div>
        </div>
    );
};
